import { POWER_STYLE, swatchAt } from "./palette";
import type { BreakoutView, PowerId } from "./types";

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

const heart = (ctx: CanvasRenderingContext2D, s: number) => {
  ctx.beginPath();
  ctx.moveTo(0, s * 0.32);
  ctx.bezierCurveTo(-s * 0.95, -s * 0.28, -s * 0.2, -s * 0.95, 0, -s * 0.38);
  ctx.bezierCurveTo(s * 0.2, -s * 0.95, s * 0.95, -s * 0.28, 0, s * 0.32);
  ctx.closePath();
};

const drawToken = (
  ctx: CanvasRenderingContext2D,
  id: PowerId,
  r: number,
  time: number,
  phase: number,
  dark: boolean,
) => {
  const style = POWER_STYLE[id];
  const pulse = 0.75 + 0.25 * Math.sin(time * 5 + phase);
  ctx.save();
  ctx.rotate(Math.sin(time * 1.6 + phase) * 0.18);

  ctx.fillStyle = style.glow;
  ctx.globalAlpha = (dark ? 0.38 : 0.28) * pulse;
  ctx.beginPath();
  ctx.arc(0, 0, r * 1.85, 0, Math.PI * 2);
  ctx.fill();

  ctx.globalAlpha = 1;
  const body = ctx.createRadialGradient(-r * 0.3, -r * 0.4, r * 0.2, 0, 0, r);
  body.addColorStop(0, "#ffffff");
  body.addColorStop(0.18, style.glow);
  body.addColorStop(1, style.core);
  ctx.fillStyle = body;

  const glyph = () => {
    ctx.fillStyle = style.ink;
    ctx.strokeStyle = style.ink;
    ctx.lineWidth = Math.max(1.2, r * 0.14);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  if (id === "expand") {
    roundRect(ctx, -r * 1.35, -r * 0.42, r * 2.7, r * 0.84, r * 0.42);
    ctx.fill();
    glyph();
    ctx.beginPath();
    ctx.moveTo(-r * 0.7, 0);
    ctx.lineTo(-r * 0.15, -r * 0.22);
    ctx.lineTo(-r * 0.15, r * 0.22);
    ctx.closePath();
    ctx.moveTo(r * 0.7, 0);
    ctx.lineTo(r * 0.15, -r * 0.22);
    ctx.lineTo(r * 0.15, r * 0.22);
    ctx.closePath();
    ctx.fill();
  } else if (id === "shrink") {
    ctx.beginPath();
    ctx.moveTo(0, -r * 1.05);
    ctx.lineTo(r * 0.42, r * 0.85);
    ctx.lineTo(-r * 0.42, r * 0.85);
    ctx.closePath();
    ctx.fill();
    glyph();
    ctx.beginPath();
    ctx.moveTo(-r * 0.16, -r * 0.05);
    ctx.lineTo(r * 0.16, -r * 0.05);
    ctx.moveTo(0, -r * 0.22);
    ctx.lineTo(0, r * 0.28);
    ctx.stroke();
  } else if (id === "multi") {
    const spots = [
      [-r * 0.38, r * 0.22],
      [r * 0.38, r * 0.22],
      [0, -r * 0.42],
    ];
    for (const [sx, sy] of spots) {
      ctx.beginPath();
      ctx.arc(sx, sy, r * 0.48, 0, Math.PI * 2);
      ctx.fill();
    }
    glyph();
    for (const [sx, sy] of spots) {
      ctx.beginPath();
      ctx.arc(sx, sy, r * 0.14, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (id === "slow") {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.95, 0, Math.PI * 2);
    ctx.fill();
    glyph();
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.62, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -r * 0.38);
    ctx.moveTo(0, 0);
    ctx.lineTo(r * 0.24, r * 0.08);
    ctx.stroke();
  } else if (id === "haste") {
    ctx.beginPath();
    ctx.moveTo(-r * 0.15, -r * 0.95);
    ctx.lineTo(r * 0.55, -r * 0.08);
    ctx.lineTo(r * 0.05, -r * 0.08);
    ctx.lineTo(r * 0.2, r * 0.95);
    ctx.lineTo(-r * 0.55, 0.02);
    ctx.lineTo(-r * 0.02, 0.02);
    ctx.closePath();
    ctx.fill();
  } else if (id === "laser") {
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(r * 0.72, 0);
    ctx.lineTo(0, r);
    ctx.lineTo(-r * 0.72, 0);
    ctx.closePath();
    ctx.fill();
    glyph();
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.42);
    ctx.lineTo(0, r * 0.42);
    ctx.stroke();
  } else if (id === "pierce") {
    ctx.beginPath();
    ctx.moveTo(0, r * 0.95);
    ctx.bezierCurveTo(r * 0.9, r * 0.15, r * 0.55, -r * 0.7, 0, -r);
    ctx.bezierCurveTo(-r * 0.55, -r * 0.7, -r * 0.9, r * 0.15, 0, r * 0.95);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.beginPath();
    ctx.ellipse(-r * 0.12, -r * 0.15, r * 0.16, r * 0.28, -0.4, 0, Math.PI * 2);
    ctx.fill();
  } else if (id === "life") {
    ctx.save();
    ctx.translate(0, r * 0.08);
    heart(ctx, r * 1.05);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.beginPath();
    ctx.ellipse(-r * 0.28, -r * 0.28, r * 0.16, r * 0.1, -0.6, 0, Math.PI * 2);
    ctx.fill();
  } else if (id === "sticky") {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.92, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = r * 0.18;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.48, 0, Math.PI * 2);
    ctx.stroke();
  } else if (id === "shield") {
    ctx.beginPath();
    ctx.moveTo(0, r * 0.95);
    ctx.lineTo(-r * 0.78, r * 0.15);
    ctx.lineTo(-r * 0.78, -r * 0.35);
    ctx.quadraticCurveTo(0, -r * 1.15, r * 0.78, -r * 0.35);
    ctx.lineTo(r * 0.78, r * 0.15);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.lineWidth = r * 0.1;
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.35);
    ctx.lineTo(0, r * 0.42);
    ctx.moveTo(-r * 0.32, r * 0.02);
    ctx.lineTo(r * 0.32, r * 0.02);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(0, r * 0.08, r * 0.82, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = style.ink;
    ctx.lineWidth = r * 0.12;
    ctx.beginPath();
    ctx.moveTo(r * 0.15, -r * 0.62);
    ctx.quadraticCurveTo(r * 0.55, -r * 1.05, r * 0.2, -r * 1.15);
    ctx.stroke();
    ctx.fillStyle = "#fef3c7";
    ctx.beginPath();
    ctx.arc(r * 0.28, -r * 1.12, r * 0.14, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
};

const drawBricks = (ctx: CanvasRenderingContext2D, view: BreakoutView, time: number) => {
  for (const brick of view.bricks) {
    if (!brick.alive) continue;
    const ease = 1 - (1 - brick.enter) ** 3;
    if (ease <= 0.02) continue;
    const sw = swatchAt(brick.color);
    const x = brick.x + brick.ox;
    const y = brick.y + brick.oy + (1 - ease) * -32;
    ctx.save();
    ctx.globalAlpha = ease * (view.dark ? 0.94 : 0.96);

    ctx.fillStyle = sw.glow;
    ctx.globalAlpha = ease * (view.dark ? 0.28 : 0.14);
    roundRect(ctx, x - 3, y - 3, brick.w + 6, brick.h + 6, 9);
    ctx.fill();

    ctx.globalAlpha = ease * (view.dark ? 0.96 : 0.98);
    const fill = ctx.createLinearGradient(x, y, x + brick.w, y + brick.h);
    fill.addColorStop(0, sw.light);
    fill.addColorStop(0.42, sw.base);
    fill.addColorStop(1, sw.dark);
    ctx.fillStyle = fill;
    roundRect(ctx, x, y, brick.w, brick.h, 7);
    ctx.fill();

    const sheen = 0.28 + 0.22 * Math.sin(time * 1.7 + brick.col * 0.55 + brick.row * 0.35);
    ctx.globalAlpha = ease * sheen;
    const shine = ctx.createLinearGradient(x, y, x, y + brick.h * 0.62);
    shine.addColorStop(0, "rgba(255,255,255,0.85)");
    shine.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = shine;
    roundRect(ctx, x + 2, y + 1.5, brick.w - 4, brick.h * 0.46, 5);
    ctx.fill();

    if (brick.kind === "gold") {
      const sweep = ((time * 0.35 + brick.col * 0.05) % 1) * (brick.w + 20) - 10;
      ctx.save();
      roundRect(ctx, x, y, brick.w, brick.h, 7);
      ctx.clip();
      const band = ctx.createLinearGradient(x + sweep, y, x + sweep + 16, y + brick.h);
      band.addColorStop(0, "rgba(255,255,255,0)");
      band.addColorStop(0.5, "rgba(255,255,255,0.75)");
      band.addColorStop(1, "rgba(255,255,255,0)");
      ctx.globalAlpha = ease * 0.85;
      ctx.fillStyle = band;
      ctx.fillRect(x, y, brick.w, brick.h);
      ctx.restore();
    }

    if (brick.kind === "armored") {
      ctx.globalAlpha = ease * 0.85;
      ctx.fillStyle = "rgba(15,23,42,0.35)";
      ctx.beginPath();
      ctx.arc(x + 7, y + brick.h / 2, 1.6, 0, Math.PI * 2);
      ctx.arc(x + brick.w - 7, y + brick.h / 2, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = ease * (view.dark ? 0.55 : 0.4);
    ctx.strokeStyle = "rgba(255,255,255,0.8)";
    ctx.lineWidth = 1;
    roundRect(ctx, x + 0.5, y + 0.5, brick.w - 1, brick.h - 1, 7);
    ctx.stroke();

    if (brick.maxHp > 1) {
      const pipW = 4;
      const gap = 3;
      const total = brick.maxHp * pipW + (brick.maxHp - 1) * gap;
      let px = x + (brick.w - total) / 2;
      const py = y + brick.h - 4.5;
      for (let i = 0; i < brick.maxHp; i += 1) {
        ctx.globalAlpha = ease * (i < brick.hp ? 0.9 : 0.28);
        ctx.fillStyle = "#ffffff";
        roundRect(ctx, px, py, pipW, 2.2, 1);
        ctx.fill();
        px += pipW + gap;
      }
    }

    if (brick.flash > 0) {
      ctx.globalAlpha = ease * brick.flash * 0.72;
      ctx.fillStyle = "#ffffff";
      roundRect(ctx, x, y, brick.w, brick.h, 7);
      ctx.fill();
    }

    ctx.restore();
  }
};

const drawBalls = (ctx: CanvasRenderingContext2D, view: BreakoutView) => {
  for (const ball of view.balls) {
    if (!ball.alive) continue;
    const pierce = view.pierceT > 0 && !ball.stuck;
    const glow = pierce ? "#fb923c" : view.dark ? "#c4b5fd" : "#7c3aed";

    ctx.save();
    for (let i = 0; i < ball.trail.length; i += 1) {
      const point = ball.trail[i];
      const t = (i + 1) / ball.trail.length;
      ctx.globalAlpha = t * t * (view.dark ? 0.45 : 0.28);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(point.x, point.y, ball.r * (0.35 + t * 0.7), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    ctx.save();
    ctx.translate(ball.x, ball.y);
    const sx = 1 + ball.squash * 0.4;
    const sy = 1 - ball.squash * 0.28;
    ctx.scale(sx, sy);
    ctx.shadowColor = glow;
    ctx.shadowBlur = view.dark ? 22 + view.heat * 26 : 12;

    const body = ctx.createRadialGradient(
      -ball.r * 0.35,
      -ball.r * 0.4,
      ball.r * 0.15,
      0,
      0,
      ball.r,
    );
    if (view.dark) {
      body.addColorStop(0, "#ffffff");
      body.addColorStop(0.45, pierce ? "#fdba74" : "#e9d5ff");
      body.addColorStop(1, pierce ? "#ea580c" : "#7c3aed");
    } else {
      body.addColorStop(0, "#ffffff");
      body.addColorStop(0.4, pierce ? "#fb923c" : "#a78bfa");
      body.addColorStop(1, pierce ? "#9a3412" : "#5b21b6");
    }
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(0, 0, ball.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.beginPath();
    ctx.ellipse(-ball.r * 0.28, -ball.r * 0.32, ball.r * 0.28, ball.r * 0.18, -0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
};

const drawPaddle = (ctx: CanvasRenderingContext2D, view: BreakoutView, time: number) => {
  const { paddle } = view;
  const x = paddle.x - paddle.w / 2;
  const idle = view.balls.some((ball) => ball.alive && ball.stuck);
  const pulse = idle ? 0.45 + 0.55 * Math.sin(time * 4.2) : 0.2;
  const h = paddle.h;

  ctx.save();
  ctx.fillStyle = paddle.tint;
  ctx.globalAlpha = view.dark ? 0.28 + pulse * 0.25 : 0.16 + pulse * 0.12;
  ctx.beginPath();
  ctx.ellipse(paddle.x, paddle.y + h + 7, paddle.w * 0.42, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.globalAlpha = 1;
  ctx.shadowColor = paddle.tint;
  ctx.shadowBlur = view.dark ? 18 + pulse * 10 : 8;
  const fill = ctx.createLinearGradient(x, paddle.y, x, paddle.y + h);
  if (view.dark) {
    fill.addColorStop(0, "#ffffff");
    fill.addColorStop(0.35, paddle.tint);
    fill.addColorStop(1, "#312e81");
  } else {
    fill.addColorStop(0, "#ffffff");
    fill.addColorStop(0.4, "#e0e7ff");
    fill.addColorStop(1, "#312e81");
  }
  ctx.fillStyle = fill;
  roundRect(ctx, x, paddle.y, paddle.w, h, h / 2);
  ctx.fill();
  ctx.shadowBlur = 0;

  ctx.globalAlpha = 0.55;
  const gloss = ctx.createLinearGradient(x, paddle.y, x, paddle.y + h * 0.55);
  gloss.addColorStop(0, "rgba(255,255,255,0.85)");
  gloss.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gloss;
  roundRect(ctx, x + 3, paddle.y + 1.5, paddle.w - 6, h * 0.45, h / 2);
  ctx.fill();

  if (paddle.flash > 0) {
    ctx.globalAlpha = paddle.flash * 0.65;
    ctx.fillStyle = "#ffffff";
    roundRect(ctx, x, paddle.y, paddle.w, h, h / 2);
    ctx.fill();
  }

  if (view.laserT > 0) {
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = "#f5d0fe";
    ctx.shadowColor = "#e879f9";
    ctx.shadowBlur = 12;
    for (const side of [-0.3, 0.3]) {
      ctx.beginPath();
      ctx.arc(paddle.x + paddle.w * side, paddle.y + 2, 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  if (view.combo >= 2) {
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = view.dark ? "#f5d0fe" : "#6d28d9";
    ctx.font = "700 12px Inter, ui-sans-serif, system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(`×${view.combo}`, paddle.x, paddle.y - 8);
  }
  ctx.restore();
};

const drawHud = (ctx: CanvasRenderingContext2D, view: BreakoutView) => {
  ctx.save();
  ctx.font = "600 14px Inter, ui-sans-serif, system-ui";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = view.dark ? "rgba(255,255,255,0.78)" : "rgba(30,27,75,0.72)";
  ctx.fillText(String(view.score), 22, 26);

  const textW = ctx.measureText(String(view.score)).width;
  let hx = 22 + textW + 14;
  for (let i = 0; i < view.lives; i += 1) {
    ctx.save();
    ctx.translate(hx + 7, 27);
    ctx.fillStyle = i === view.lives - 1 && view.veilWarm ? "#fb7185" : "#e11d48";
    ctx.globalAlpha = view.dark ? 0.95 : 0.88;
    heart(ctx, 8);
    ctx.fill();
    ctx.restore();
    hx += 16;
  }

  ctx.textAlign = "right";
  ctx.globalAlpha = view.dark ? 0.45 : 0.4;
  ctx.font = "600 12px Inter, ui-sans-serif, system-ui";
  ctx.fillStyle = view.dark ? "#ffffff" : "#312e81";
  ctx.fillText(String(view.level).padStart(2, "0"), view.w - 22, 26);
  ctx.restore();
};

/** Şeffaf sahne: ızgara alttan görünür, ışıklar ve tuğlalar onun üstüne çizilir. */
export const paintBreakout = (
  ctx: CanvasRenderingContext2D,
  view: BreakoutView,
  time: number,
) => {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();

  if (view.w < 32 || view.h < 32) return;

  ctx.save();
  if (!view.reduced && view.shake > 0.25) {
    ctx.translate(
      (Math.random() - 0.5) * view.shake,
      (Math.random() - 0.5) * view.shake * 0.65,
    );
  }

  const wash = ctx.createRadialGradient(
    view.w * 0.5,
    view.h * 0.18,
    20,
    view.w * 0.5,
    view.h * 0.2,
    view.w * 0.55,
  );
  wash.addColorStop(0, view.dark ? "rgba(139,92,246,0.16)" : "rgba(124,58,237,0.07)");
  wash.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, view.w, view.h);

  for (const ball of view.balls) {
    if (!ball.alive) continue;
    const pierce = view.pierceT > 0 && !ball.stuck;
    const radius = 120 + view.heat * 90;
    const pool = ctx.createRadialGradient(ball.x, ball.y, 0, ball.x, ball.y, radius);
    if (pierce) {
      pool.addColorStop(0, view.dark ? "rgba(251,146,60,0.38)" : "rgba(234,88,12,0.16)");
    } else if (view.dark) {
      pool.addColorStop(0, "rgba(196,181,253,0.5)");
    } else {
      pool.addColorStop(0, "rgba(124,58,237,0.14)");
    }
    pool.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = pool;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  drawBricks(ctx, view, time);

  if (view.shieldT > 0) {
    const y = view.paddle.y + view.paddle.h + 18;
    const alpha = 0.35 + 0.25 * Math.sin(time * 8);
    ctx.save();
    ctx.strokeStyle = `rgba(45,212,191,${alpha})`;
    ctx.lineWidth = 3;
    ctx.shadowColor = "#2dd4bf";
    ctx.shadowBlur = view.dark ? 16 : 6;
    ctx.beginPath();
    ctx.moveTo(18, y);
    for (let x = 18; x <= view.w - 18; x += 8) {
      const wave = Math.sin(x * 0.08 + time * 6) * 3;
      ctx.lineTo(x, y + wave);
    }
    ctx.stroke();
    ctx.restore();
  }

  for (const power of view.powers) {
    if (!power.alive) continue;
    ctx.save();
    const drift = Math.sin(time * 3 + power.phase) * 6;
    ctx.translate(power.x + drift, power.y);
    drawToken(ctx, power.id, power.r, time, power.phase, view.dark);
    ctx.restore();
  }

  for (const particle of view.particles) {
    const t = Math.max(0, particle.life / particle.max);
    ctx.save();
    ctx.globalAlpha = t;
    ctx.translate(particle.x, particle.y);
    if (particle.kind === "shard") {
      ctx.rotate(particle.rot);
      ctx.fillStyle = particle.color;
      ctx.fillRect(-particle.size, -particle.size * 0.6, particle.size * 2, particle.size * 1.2);
    } else if (particle.kind === "glow") {
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, particle.size * 3);
      g.addColorStop(0, particle.color);
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, particle.size * 3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.strokeStyle = particle.color;
      ctx.lineWidth = particle.size;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-particle.vx * 0.03, -particle.vy * 0.03);
      ctx.stroke();
    }
    ctx.restore();
  }

  if (view.beams.length) {
    ctx.save();
    ctx.globalCompositeOperation = view.dark ? "lighter" : "source-over";
    for (const beam of view.beams) {
      const t = Math.max(0, beam.life / beam.max);
      const grad = ctx.createLinearGradient(beam.x, beam.y0, beam.x, beam.y1);
      grad.addColorStop(0, `rgba(250,232,255,${t})`);
      grad.addColorStop(1, `rgba(217,70,239,${t * 0.2})`);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 5 * t + 1;
      ctx.lineCap = "round";
      ctx.shadowColor = "#e879f9";
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.moveTo(beam.x, beam.y0);
      ctx.lineTo(beam.x, beam.y1);
      ctx.stroke();
    }
    ctx.restore();
  }

  drawPaddle(ctx, view, time);
  drawBalls(ctx, view);

  for (const ring of view.rings) {
    const t = Math.max(0, ring.life / ring.max);
    ctx.save();
    ctx.globalAlpha = t;
    ctx.strokeStyle = ring.color;
    ctx.lineWidth = ring.width * t;
    ctx.beginPath();
    ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();

  for (const floater of view.floaters) {
    const age = 1 - floater.life / floater.max;
    const alpha = age < 0.12 ? age / 0.12 : floater.life / floater.max;
    ctx.save();
    ctx.globalAlpha = Math.max(0, alpha);
    ctx.fillStyle = floater.color;
    ctx.font = `700 ${floater.size}px Inter, ui-sans-serif, system-ui`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(floater.text, floater.x, floater.y);
    ctx.restore();
  }

  drawHud(ctx, view);

  if (view.veil > 0.02) {
    ctx.save();
    ctx.fillStyle = view.veilWarm
      ? `rgba(244,63,94,${view.veil * 0.22})`
      : `rgba(255,255,255,${view.veil * (view.dark ? 0.08 : 0.2)})`;
    ctx.fillRect(0, 0, view.w, view.h);
    ctx.restore();
  }
};
